/**
 * Every path in the application, named once.
 *
 * <h2>Why a constant and not a string at each call site</h2>
 *
 * A route typed into a `<Link>` is a string the compiler cannot check. Renaming a
 * page then leaves working-looking links that 404 at runtime, and nothing finds
 * them but a person clicking every one.
 *
 * Named here, a rename is one edit and every stale reference fails to compile.
 */
export const ROUTES = {
  home: '/',

  // The paths the ported navigation already links to. Renamed from /sign-in and
  // /sign-up: those were mine, and every link in the copied header and footer
  // pointed at these -- so "Login" in the navigation went to the not-found page.
  // Matching the application being replaced is also what keeps any existing
  // bookmark and email working.
  signIn: '/login',
  signUp: '/signup',
  activate: '/activate',
  /**
   * Where an INVITED account's link lands — the one that asks for a password.
   *
   * <p>A separate page from `activate`, because the two flows genuinely differ: somebody who
   * registered chose their password at sign-up, and somebody invited by an administrator has
   * none at all (`team.md` TM-81). Which link is sent is decided on the server; see
   * `activation.md` AC-10d.
   */
  setInvitedPassword: '/activate/set-password',
  resendActivation: '/activate/resend',

  // Named to match the ported navigation map in utils/navigation.ts, which the
  // copied sign-in page already routes "Forgot Password?" through. A different
  // path here would leave that link pointing at the not-found page.
  forgotPassword: '/forgot-password',
  resetPassword: '/reset-password',

  /**
   * Asks for a sign-in link, the password-free way in.
   *
   * <p>A page of its own rather than a mode on `/login`, matching
   * `forgotPassword` next door: the success state is a whole panel that has to
   * say "if that address has an account", and a toggle inside the sign-in form
   * would have to swap the form out to show it.
   */
  magicLinkRequest: '/magic-link',

  /**
   * Where a sign-in link lands.
   *
   * <p>`/magic` is the previous product's own path, kept so a link already in
   * somebody's inbox still works — and it is what the server puts in the mail
   * (`xtremand.mail.magic-link-url`), so the two have to agree.
   */
  magicLink: '/magic',

  dashboard: '/dashboard',

  // The rest of the signed-in application. Every one of these renders a
  // work-in-progress page today, and that is deliberate: the shell, the menu and
  // the locked state are the thing being built, and a menu whose items go
  // nowhere cannot be navigated to test any of it.
  //
  // The paths are the real ones rather than placeholders, so the screens land
  // here later without a rename -- and a route typed into a <Link> is a string
  // the compiler cannot check, which is the whole reason this file exists.
  aiAgent: '/agents',

  // The two agents the AI Agents row expands into, registered 8 October 2026 when the
  // sidebar gained that submenu. Both paths are the previous system's own -- its
  // `AppNav.tsx` sends "SEO Agent" to `/agents/seo` and "LinkedIn Agent" to
  // `/agents/linkedin-dashboard` -- so a deep link that worked there works here, and the
  // ported pages land on these paths without a rename.
  //
  // Neither page is ported yet: both resolve to the work-in-progress screen like every
  // other unbuilt product screen. `/agents` itself remains a separate screen rather than
  // redirecting to either, because it fronts both products and choosing one for somebody
  // would be guessing.
  seoAgent: '/agents/seo',

  /**
   * The SEO agent's setup: the welcome page (company details and business goals, step 1)
   * and the wizard that carries on from it (`?step=2` to `?step=6`). Both are the previous
   * system's paths minus its `/agents/setup`, which is nested under the agent here.
   */
  seoAgentWelcome: '/agents/seo/welcome',
  seoAgentSetup: '/agents/seo/setup',

  /**
   * One tab of the SEO Agent dashboard: `/agents/seo/strategy`, `/agents/seo/keywords`… The
   * Overview tab is `seoAgent` itself. A pattern, registered in `screens.ts` so the product guard
   * covers every tab; link with `routeTo.seoAgentTab(segment)`.
   */
  seoAgentTab: '/agents/seo/:tab',
  /**
   * One task's page, and one page a task changed. Two segments deep, so `:tab` does not match
   * them: each is registered in `screens.ts` on its own. Link with `routeTo.seoAgentTask`.
   */
  seoAgentTask: '/agents/seo/activity-task/:taskId',
  seoAgentTaskChanges: '/agents/seo/activity-task/:taskId/changes/:postId',
  /**
   * The agent's settings: its configuration (goals, offerings, competitors and publishing
   * capacity, notes) and its connected services (`?tab=connections`). The previous system's
   * `/agents/seo-settings`, nested under the agent like the setup. A static segment, so it wins
   * over `seoAgentTab`; registered in `screens.ts` ahead of that pattern.
   */
  seoAgentSettings: '/agents/seo/settings',
  /**
   * One Create page or post request, opened from the Content tab. Two segments deep, so `:tab` does
   * not match it: registered in `screens.ts` on its own. Link with `routeTo.seoAgentCustomContent`.
   * The Create page itself is the `create` tab segment.
   */
  seoAgentCustomContent: '/agents/seo/custom-content/:runId',
  linkedinAgent: '/agents/linkedin-dashboard',
  // The agent's settings: edit the saved configuration, the AI team and the references after launch.
  linkedinSettings: '/agents/linkedin-settings',
  // The LinkedIn onboarding journey, in the previous system's own paths. Only the connect step is
  // built; the others open a placeholder until their screens are ported.
  linkedinWelcome: '/agents/linkedin-welcome',
  linkedinCompanyDetails: '/agents/linkedin-company-details',
  linkedinConfiguration: '/agents/linkedin-configuration',
  linkedinLaunch: '/agents/linkedin-launch',
  linkedinConnect: '/agents/linkedin-connect',
  // The reference profiles panel of the previous settings screen, on its own until that screen is ported.
  linkedinReferences: '/agents/linkedin-reference-profiles',
  linkedinReview: '/agents/linkedin-review',
  // The prospect pipeline, in the previous system's own paths.
  linkedinProspects: '/agents/linkedin-prospects',
  // The plan the agent writes for the account, day by day, and what a person does to it.
  linkedinStrategy: '/agents/linkedin-strategy',
  // What the agent drafted for a person to approve: replies, first messages and comments.
  linkedinConversations: '/agents/linkedin-conversations',
  // The company posts: draft, review, approve and follow them, and everything waiting for approval in one place.
  linkedinContent: '/agents/linkedin-content',
  linkedinApprovals: '/agents/linkedin-approvals',
  // What the agent has done, as a feed with its overview, and the company's analytics over it.
  linkedinActivity: '/agents/linkedin-activity',
  linkedinAnalytics: '/agents/linkedin-analytics',
  linkedinProspectDetail: '/agents/linkedin-prospects/:prospectId',

  brandAnalyser: '/assets/analyser',

  /**
   * One analysed brand, by its slug. **The pattern**, not a usable URL: link with
   * `routeTo.brandOverview(slug)` at the foot of this file.
   *
   * <p>The previous system's own path, kept so a bookmark and a shared link still land
   * somewhere. Reached from the Brand Analyser list and from the brand card.
   */
  brandOverview: '/BrandOverview/:brandSlug',

  /** Writing a review of a brand. The previous system's path, with `?brand=` and `&brandId=`. */
  writeReview: '/write-review',
  collections: '/assets/collections',
  reviews: '/reviews',

  /**
   * The Reviews & Ratings screens. The previous system's own paths, so a bookmark and an emailed
   * link still land. `reviewsMine` and `writeReview` name no product -- anybody signed in may
   * review a brand and read their own reviews -- while the other three are behind
   * `reviews_rankings`.
   */
  reviewsMine: '/reviews/myreviews',
  reviewsResponses: '/app/reviews/responses',
  reviewsAggregation: '/app/reviews/aggregation',
  reviewForms: '/account/review-forms',
  emailWarmup: '/email-warmup',

  /**
   * One mailbox, by its **public UUID**.
   *
   * <p>A pattern, not a URL: link with `routeTo.emailWarmupAccount(id)` at the foot of this
   * file. The previous system base64-encoded the EMAIL into this path to hide its numeric
   * ids; there is nothing to hide here, because `MailAccount.id` is already the external
   * identifier and the internal `BIGINT` is published nowhere.
   */
  emailWarmupAccount: '/email-warmup/account/:externalId',

  /**
   * Connecting a mailbox.
   *
   * <p>The previous system's own path, kept so a bookmark and a support article still
   * land somewhere — `app.xtremand.com/email-warmup/create-account`. It is a sibling of
   * the list rather than a child of a mailbox, because nothing exists yet to be a child of.
   */
  emailWarmupCreate: '/email-warmup/create-account',

  /**
   * The Email Warmup plans, and the way to ask for another. Behind the same product as the list it is
   * reached from. The previous system's `/app/email-warmup/billing`.
   */
  emailWarmupBilling: '/email-warmup/billing',
  developerTools: '/developer',

  /**
   * The rest of Developer Tools. The previous system's own paths, so a bookmark and a shared
   * link still land. All of them are behind `brand_api` except the request-access form, which
   * is how somebody without it asks. `apiKeyDetail` and `mcpProtocolDetail` are patterns: the
   * old screens build their own links to them.
   */
  developerPricing: '/developer/pricing',
  apiConsole: '/api-console',
  apiKeyDetail: '/api-key-detail/:keyId',
  developerApiDocumentation: '/developer/api-documentation',
  developerMcpDocumentation: '/developer/mcp-docs',
  brandApiDocs: '/app/brand-api-docs',
  developerMcp: '/developer/mcp',
  mcpProtocolDetail: '/app/mcp-protocol-detail/:protocolId',
  developerIntegrations: '/app/developer/integrations',
  developerRequestAccess: '/app/developer/request-access',

  /*
   * Added 3 October 2026 with its menu row. The product has been on sale since
   * 26 September and had an endpoint from 30 September, and nothing in this
   * application named it -- so somebody assigned it had no way to reach it and no
   * sign it existed. `placeholderRoutes` is generated from NAV_ITEMS, so adding the
   * row is what gives this path a screen.
   */
  leadMeetingIntelligence: '/lead-meeting-intelligence',
  team: '/team',

  /**
   * Adding somebody, as its own page rather than a panel that unfolds above the list.
   *
   * <p>Under `/team` because it is the Team screen's own action and the URL should say so,
   * and a real route because the form has to be linkable, refreshable and cancellable — a
   * panel behind a `useState` is none of those, and closing the tab mid-form lost it with
   * nothing in the URL to come back to.
   */
  teamAdd: '/team/add',

  /**
   * Editing one person's details and access. **The pattern**, not a usable URL.
   *
   * <p>`:externalId`, not `:id`. It is the public identifier, and the URL says the same word
   * the API and the code do — `SERVICE_STANDARDS.md` §3. A segment called `id` invites an
   * internal one.
   *
   * <p>`/collection/:externalId/edit` is the shape anything else follows:
   * `/platform/receivers/:externalId/edit` would read the same way.
   *
   * <p>To LINK here, call `routeTo.teamEdit(id)` at the bottom of this file. This value is
   * what the route table matches on.
   */
  teamEdit: '/team/:externalId/edit',
  account: '/account',
  profile: '/profile',
  /** Billing & Plans: what the company pays for, module by module. The previous system's `/account/billing`. */
  billing: '/account/billing',
  /** Security Settings: the password. The previous system's `/account/security`. */
  accountSecurity: '/account/security',
  /**
   * Settings › Integrations: the tools the person has connected to their own account — Slack, today.
   * The previous system's own path, so a bookmark to it still lands here.
   */
  integrations: '/settings/integrations',
  /** Settings › Notifications: Slack, email or both. The previous system's own path. */
  notifications: '/settings/notifications',
  /**
   * The in-app notifications behind the bell. The previous system's own path, so a bookmark still
   * lands here. Not `notifications`, which is the channel setting above.
   */
  alerts: '/notifications',
  /**
   * Where Stripe Checkout sends a customer back to. The backend writes these two addresses into the
   * checkout it makes (`ModuleCheckoutService`), so they cannot be renamed without it. `session_id` and
   * `moduleKey` are in the query.
   */
  billingModuleSuccess: '/billing/module/success',
  billingCancel: '/billing/cancel',
  aiAssistant: '/ai-assistant',

  /**
   * The Support area: a landing page with three doors, and the three things behind them. Any
   * signed-in person, whatever they have been assigned, so they sit outside the product guard
   * with the team and account screens.
   */
  support: '/support',
  supportDocs: '/support/docs',
  /** One documentation page, opened from the library. Link with `routeTo.supportDoc`. */
  supportDoc: '/support/docs/:documentId',
  supportHelp: '/support/help',
  supportContact: '/support/contact',
  /**
   * Support & Documentation management: the platform operator's, like Review Management. The previous
   * system's `/admin/support`. The landing page and the three screens it leads to.
   */
  supportAdmin: '/platform/support',
  supportAdminHelp: '/platform/support/help-center',
  supportAdminTickets: '/platform/support/tickets',
  /** One ticket, for the operator. Link with `routeTo.supportAdminTicket`. */
  supportAdminTicket: '/platform/support/tickets/:ticketId',
  supportAdminDocs: '/platform/support/documentation',
  supportAdminDocNew: '/platform/support/documentation/new',
  /** Edit one documentation page. Link with `routeTo.supportAdminDocEdit`. */
  supportAdminDocEdit: '/platform/support/documentation/:documentId/edit',
  /** One ticket, opened from the Support Center's list. Link with `routeTo.supportTicket`. */
  supportTicket: '/support/tickets/:ticketId',

  /**
   * The operator's Email Warmup screen, whose main table is the receiver pool. The operator's,
   * so under `/platform/...`: the receivers belong to no customer, and changing one changes
   * warmup for every customer. That prefix is what marks a screen as the platform's rather
   * than a tenant's, and a route added later inherits the distinction by where it is put.
   *
   * <p>The three screens below it are reached from it, as they were in the previous system, and
   * are not menu rows.
   */
  warmupReceivers: '/platform/email-warmup',

  /**
   * Review Management: the questions every review form asks. The platform operator's, like the
   * receiver pool -- changing a question changes the form for every customer -- so under the same
   * prefix. The previous system's `/admin/reviews/question-builder`.
   */
  reviewQuestions: '/platform/reviews/questions',

  /**
   * Billing and plans: the operator's. What a plan costs and allows is the platform's to decide and
   * changes what every customer sees, so it sits under `/platform` with the receiver pool and the
   * review questions. The previous system's `/admin/billing/...`. `plans/:planId` is a pattern; the
   * form for a new plan is `plans/new`, listed before it so it is not read as an id.
   */
  platformBilling: '/platform/billing',
  platformBillingPlans: '/platform/billing/plans',
  platformBillingPlanNew: '/platform/billing/plans/new',
  platformBillingPlan: '/platform/billing/plans/:planId',
  platformBillingRequests: '/platform/billing/requests',

  /** The warmup templates, with the AI-assisted writer. */
  warmupTemplates: '/platform/email-warmup/templates',

  /** The kinds of warmup email, and the instruction the AI follows for each. */
  warmupTemplateCategories: '/platform/email-warmup/template-categories',

  /** The AI provider keys and models. */
  warmupApiKeys: '/platform/email-warmup/api-keys',

  /** Adds an AI provider key and its models. */
  warmupProviderConfig: '/platform/email-warmup/provider-config',

  // Where GitHub and LinkedIn send people back to. The ported "Continue with"
  // buttons already used this address; nothing matched it, so every return from
  // a consent screen -- approved or cancelled -- landed on the not-found page.
  oauthCallback: '/oauth/:provider/callback',

  notFound: '*',
} as const;

export type Route = (typeof ROUTES)[keyof typeof ROUTES];

/**
 * The URL for a parameterised route, filled in.
 *
 * <h2>Why these are not entries in `ROUTES`</h2>
 *
 * A builder put there makes `ROUTES` a mixture of strings and functions, and everything that
 * walks it has to learn the difference: `Object.values(ROUTES)` stops being a list of paths,
 * and `navItems.test.ts` — which checks that every menu entry is a published route — stops
 * compiling. One heterogeneous constant costs more than one extra export.
 *
 * <p>Each builder fills in the pattern rather than repeating it, so the path is written once
 * and a rename cannot leave the two disagreeing.
 */
export const routeTo = {
  platformBillingPlan: (planId: string) =>
    ROUTES.platformBillingPlan.replace(':planId', encodeURIComponent(planId)),
  teamEdit: (externalId: string) => ROUTES.teamEdit.replace(':externalId', externalId),
  supportAdminTicket: (ticketId: string) =>
    ROUTES.supportAdminTicket.replace(':ticketId', encodeURIComponent(ticketId)),
  supportAdminDocEdit: (documentId: string) =>
    ROUTES.supportAdminDocEdit.replace(':documentId', encodeURIComponent(documentId)),
  supportDoc: (documentId: string) =>
    ROUTES.supportDoc.replace(':documentId', encodeURIComponent(documentId)),
  supportTicket: (ticketId: string) =>
    ROUTES.supportTicket.replace(':ticketId', encodeURIComponent(ticketId)),
  seoAgentTab: (segment: string) => ROUTES.seoAgentTab.replace(':tab', segment),
  linkedinProspectDetail: (prospectId: string) =>
    ROUTES.linkedinProspectDetail.replace(':prospectId', encodeURIComponent(prospectId)),
  seoAgentTask: (taskId: string) =>
    ROUTES.seoAgentTask.replace(':taskId', encodeURIComponent(taskId)),
  seoAgentCustomContent: (runId: string) =>
    ROUTES.seoAgentCustomContent.replace(':runId', encodeURIComponent(runId)),
  seoAgentSettings: (tab?: 'connections') =>
    tab === undefined ? ROUTES.seoAgentSettings : `${ROUTES.seoAgentSettings}?tab=${tab}`,
  seoAgentTaskChanges: (taskId: string, postId: string) =>
    ROUTES.seoAgentTaskChanges
      .replace(':taskId', encodeURIComponent(taskId))
      .replace(':postId', encodeURIComponent(postId)),
  brandOverview: (brandSlug: string) =>
    ROUTES.brandOverview.replace(':brandSlug', encodeURIComponent(brandSlug)),
  emailWarmupAccount: (externalId: string) =>
    ROUTES.emailWarmupAccount.replace(':externalId', externalId),
} as const;
